import { motion } from "framer-motion";
import { Coins, Flame, Lock, Users, Wallet, Gamepad2, Copy, Check } from "lucide-react";
import { useState } from "react";

const CARDS = [
  {
    icon: Coins,
    title: "Total Supply",
    value: "1,000,000,000",
    sub: "1B tokens. One billion good boys.",
    glow: "gg-glow-gold",
    accent: "var(--jd-secondary)",
    testid: "tokenomics-supply",
  },
  {
    icon: Flame,
    title: "Buy / Sell Tax",
    value: "0 / 0",
    sub: "Zero tax. Surf in, surf out.",
    glow: "gg-glow-cyan",
    accent: "var(--jd-primary)",
    testid: "tokenomics-tax",
  },
  {
    icon: Lock,
    title: "Liquidity",
    value: "100% Burned",
    sub: "LP keys tossed to the sea floor.",
    glow: "gg-glow-pink",
    accent: "var(--jd-accent)",
    testid: "tokenomics-lp",
  },
  {
    icon: Users,
    title: "Ownership",
    value: "Renounced",
    sub: "No dev wallet. Community owned.",
    glow: "gg-glow-cyan",
    accent: "var(--jd-primary)",
    testid: "tokenomics-ownership",
  },
];

const ALLOCATIONS = [
  {
    icon: Wallet,
    title: "Team Wallet",
    pct: "4% locked + 0.5%",
    sub: "0.5% reserved for P2E buybacks.",
    address: "8cQxKjAfekivFP856RVeRJCDeaHHunuQSG4Dc5SA3KW7",
    glow: "gg-glow-cyan",
    testid: "allocation-team",
  },
  {
    icon: Gamepad2,
    title: "Game Treasury 1",
    pct: "1%",
    sub: "Funds P2E claim payouts.",
    address: "3Wa9PoJww7P2SHLEe6nBQHbaBePWMEnzbpByJgaZv9jM",
    glow: "gg-glow-pink",
    testid: "allocation-treasury-1",
  },
  {
    icon: Gamepad2,
    title: "Game Treasury 2",
    pct: "1%",
    sub: "Backup pool for high-volume runs.",
    address: "2wd5fJb7X3jYKyPaMG6n6zDRCdisc1CZ9HpbjCpR7a29",
    glow: "gg-glow-gold",
    testid: "allocation-treasury-2",
  },
];

const AddressRow = ({ address, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // noop
    }
  };
  const short = `${address.slice(0, 6)}…${address.slice(-6)}`;
  return (
    <button
      type="button"
      onClick={copy}
      className="mt-3 w-full inline-flex items-center justify-between gap-2 gg-terminal rounded-lg px-3 py-2 text-left transition-transform hover:scale-[1.02]"
      data-testid={testid}
      aria-label={`Copy ${address}`}
    >
      <span className="font-mono text-xs sm:text-sm truncate text-[var(--jd-primary)]" title={address}>{short}</span>
      {copied ? (
        <span className="inline-flex items-center gap-1 text-xs font-display uppercase text-[var(--jd-green)]">
          <Check size={14} /> Copied
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs font-display uppercase text-[var(--jd-ocean)]">
          <Copy size={14} /> Copy
        </span>
      )}
    </button>
  );
};

export const Tokenomics = () => {
  return (
    <section
      id="tokenomics"
      className="section relative bg-[var(--jd-bg)]"
      data-testid="tokenomics-section"
    >
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse, rgba(255,215,0,0.07), transparent 65%)" }}
        aria-hidden="true"
      />
      <div className="mx-auto max-w-6xl relative">
        <div className="text-left md:text-center">
          <p className="font-accent text-2xl text-[var(--jd-secondary)]">jet fuel</p>
          <h2
            className="font-display font-bold text-4xl md:text-6xl uppercase mt-2 neon-title-gold"
            data-testid="tokenomics-title"
          >
            Tokenomics
          </h2>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`gg-panel ${c.glow} rounded-2xl p-6 card-lift`}
                data-testid={c.testid}
              >
                <div
                  className="w-12 h-12 rounded-xl inline-flex items-center justify-center mb-4 border border-white/10"
                  style={{ color: c.accent, background: "rgba(255,255,255,0.03)" }}
                >
                  <Icon size={24} />
                </div>
                <p className="text-[10px] uppercase tracking-widest font-mono text-[var(--jd-ocean)]">{c.title}</p>
                <p className="font-display font-bold text-xl md:text-2xl mt-1 leading-tight break-words" style={{ color: c.accent }}>
                  {c.value}
                </p>
                <p className="mt-2 text-sm text-[var(--jd-ocean)]">{c.sub}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Allocations / Wallet breakdown */}
        <div className="mt-16" data-testid="allocations-block">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <h3 className="font-display font-bold text-2xl md:text-3xl uppercase neon-title">
              Allocations
            </h3>
            <p className="text-[var(--jd-ocean)] text-sm max-w-md">
              Transparent on-chain wallets. Tap any address to copy &amp; verify on Solscan.
            </p>
          </div>

          <div className="mt-6 grid md:grid-cols-3 gap-6">
            {ALLOCATIONS.map((a, i) => {
              const Icon = a.icon;
              return (
                <motion.div
                  key={a.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className={`gg-panel ${a.glow} rounded-2xl p-6 card-lift`}
                  data-testid={a.testid}
                >
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl border border-white/10 inline-flex items-center justify-center text-[var(--jd-primary)]" style={{ background: "rgba(255,255,255,0.03)" }}>
                      <Icon size={22} />
                    </div>
                  </div>
                  <p className="font-display font-bold text-xl uppercase mt-3 leading-tight">
                    {a.title}
                  </p>
                  <p className="mt-1 text-sm text-[var(--jd-ocean)]">{a.sub}</p>
                  <AddressRow address={a.address} testid={`${a.testid}-copy`} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Tokenomics;
